/**
 * Budgets — what a unit said it would spend, and what is left of it.
 *
 * Four figures per line, never added into one: the budget, what is committed
 * (approved requisitions, not yet paid), what is spent, and what remains. A
 * line in the red is shown in the red — the platform warns, it does not block.
 */
import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Loader2, PiggyBank, Trash2, Pencil } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from '@/components/ui/card';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import CurrencySelect from '@/components/CurrencySelect';
import { useAuthStore } from '@/lib/store';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const money = (n, ccy) => (n === null || n === undefined
  ? '—'
  : `${ccy || ''} ${Number(n).toLocaleString(undefined, {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  })}`.trim());

const BudgetBar = ({ budget, committed, spent }) => {
  const total = Number(budget) || 0;
  const pct = (v) => (total > 0 ? Math.min(100, (Number(v || 0) / total) * 100) : 0);
  const over = total > 0 && Number(committed || 0) + Number(spent || 0) > total;
  return (
    <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100">
      <div className="flex h-full">
        <div className={over ? 'bg-rose-500' : 'bg-emerald-500'}
             style={{ width: `${pct(spent)}%` }} />
        <div className={over ? 'bg-rose-300' : 'bg-indigo-400'}
             style={{ width: `${pct(committed)}%` }} />
      </div>
    </div>
  );
};

export const BudgetPanel = ({ unitId }) => {
  const { token } = useAuthStore();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(null);
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const { data, isLoading } = useQuery({
    queryKey: ['budgets', unitId],
    queryFn: async () => {
      const r = await fetch(`${API_URL}/api/finance/budgets?unit_id=${unitId}`, { headers });
      if (!r.ok) throw new Error('Could not load the budget');
      return r.json();
    },
    enabled: !!token && !!unitId,
    retry: false,
  });

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['budgets'] });
    queryClient.invalidateQueries({ queryKey: ['requisitions'] });
  };

  const retire = useMutation({
    mutationFn: async (line) => {
      const r = await fetch(`${API_URL}/api/finance/budgets/${line.id}`, {
        method: 'DELETE', headers, body: JSON.stringify({ reason: 'No longer in use' }),
      });
      const out = await r.json();
      if (!r.ok) throw new Error(out.detail || 'That could not be done');
      return out;
    },
    onSuccess: () => { refresh(); toast.success('Line taken out of use'); },
    onError: (e) => toast.error(e.message),
  });

  const lines = data?.lines || [];
  const totals = Object.entries(data?.totals_by_currency || {});

  return (
    <div className="space-y-4" data-testid="budget-panel">
      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              <PiggyBank className="h-5 w-5 text-emerald-600" />
              Budget {data?.fiscal_year ? `· ${data.fiscal_year}` : ''}
            </CardTitle>
            <CardDescription>
              What this unit means to spend — and what the warnings measure against
            </CardDescription>
          </div>
          <Button size="sm" disabled={!unitId} data-testid="budget-new-btn"
                  onClick={() => setEditing({ line: '', amount: '', currency: 'USD' })}>
            Add a line
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          {!unitId ? (
            <p className="text-sm text-slate-500" data-testid="budget-pick-unit">
              Choose a unit to set its budget.
            </p>
          ) : null}

          {totals.length ? (
            <div className="grid gap-3 sm:grid-cols-3" data-testid="budget-totals">
              {totals.map(([ccy, t]) => (
                <div key={ccy} className="rounded-lg border p-3">
                  <p className="text-[10px] uppercase tracking-wide text-slate-400">{ccy}</p>
                  <dl className="mt-1 space-y-0.5 text-sm">
                    <div className="flex justify-between">
                      <dt className="text-slate-500">Budget</dt>
                      <dd>{money(t.budget, ccy)}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-slate-500">Committed</dt>
                      <dd>{money(t.committed, ccy)}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-slate-500">Spent</dt>
                      <dd>{money(t.spent, ccy)}</dd>
                    </div>
                    <div className="flex justify-between border-t pt-0.5">
                      <dt className="font-medium text-slate-600">Left</dt>
                      <dd className={`font-semibold ${Number(t.available) < 0
                        ? 'text-rose-600' : 'text-emerald-700'}`}>
                        {money(t.available, ccy)}
                      </dd>
                    </div>
                  </dl>
                </div>
              ))}
            </div>
          ) : null}

          {isLoading ? (
            <p className="flex items-center gap-2 py-6 text-sm text-slate-500">
              <Loader2 className="h-4 w-4 animate-spin" />Looking…
            </p>
          ) : !lines.length && unitId ? (
            <p className="py-6 text-center text-sm text-slate-500" data-testid="budget-empty">
              This unit keeps no budget yet, so nothing limits its spending.
            </p>
          ) : lines.length ? (
            <div className="rounded-lg border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Line</TableHead>
                    <TableHead className="text-right">Budget</TableHead>
                    <TableHead className="text-right">Committed</TableHead>
                    <TableHead className="text-right">Spent</TableHead>
                    <TableHead className="text-right">Left</TableHead>
                    <TableHead className="w-28" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {lines.map((l, i) => (
                    <TableRow key={l.id} data-testid={`budget-row-${i}`}>
                      <TableCell>
                        <p className="font-medium text-slate-800">{l.line}</p>
                        <div className="mt-1 w-40">
                          <BudgetBar budget={l.budget} committed={l.committed} spent={l.spent} />
                        </div>
                        {l.amended ? (
                          <span className="text-[11px] text-slate-500"
                                data-testid={`budget-amended-${i}`}>
                            amended {l.amended}× · {l.last_change?.reason || 'no reason given'}
                          </span>
                        ) : null}
                      </TableCell>
                      <TableCell className="text-right">{money(l.budget, l.currency)}</TableCell>
                      <TableCell className="text-right text-indigo-700">
                        {money(l.committed, l.currency)}
                      </TableCell>
                      <TableCell className="text-right">{money(l.spent, l.currency)}</TableCell>
                      <TableCell className="text-right">
                        <span className={Number(l.available) < 0
                          ? 'font-semibold text-rose-600' : 'font-medium text-emerald-700'}>
                          {money(l.available, l.currency)}
                        </span>
                        {Number(l.available) < 0 ? (
                          <Badge className="ml-1 bg-rose-100 text-rose-700"
                                 data-testid={`budget-over-${i}`}>over</Badge>
                        ) : null}
                      </TableCell>
                      <TableCell className="space-x-1 text-right">
                        <Button size="sm" variant="ghost" data-testid={`budget-edit-${i}`}
                                onClick={() => setEditing({
                                  id: l.id, line: l.line, amount: String(l.budget ?? ''),
                                  currency: l.currency, notes: l.notes, existing: true,
                                })}>
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" data-testid={`budget-retire-${i}`}
                                onClick={() => retire.mutate(l)}>
                          <Trash2 className="h-3.5 w-3.5 text-rose-500" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : null}
        </CardContent>
      </Card>

      {editing ? (
        <LineDialog unitId={unitId} initial={editing} onClose={() => setEditing(null)}
                    onSaved={() => { setEditing(null); refresh(); }} />
      ) : null}
    </div>
  );
};

const LineDialog = ({ unitId, initial, onClose, onSaved }) => {
  const { token } = useAuthStore();
  const [form, setForm] = useState({
    line: initial.line || '', amount: initial.amount || '',
    currency: initial.currency || 'USD', notes: initial.notes || '', reason: '',
  });
  const amending = !!initial.existing;

  const save = useMutation({
    mutationFn: async () => {
      const r = await fetch(`${API_URL}/api/finance/budgets`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, unit_id: unitId, amount: Number(form.amount) }),
      });
      const out = await r.json();
      if (!r.ok) throw new Error(out.detail || 'Could not save that');
      return out;
    },
    onSuccess: () => { toast.success(amending ? 'Budget amended' : 'Line set'); onSaved(); },
    onError: (e) => toast.error(e.message),
  });

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="budget-form">
        <DialogHeader>
          <DialogTitle className="text-base">
            {amending ? 'Amend this line' : 'Add a budget line'}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Line</Label>
            <Input value={form.line} data-testid="budget-line-name" disabled={amending}
                   placeholder="Sound equipment"
                   onChange={(e) => setForm({ ...form, line: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Amount for the year</Label>
              <Input type="number" value={form.amount} data-testid="budget-amount"
                     onChange={(e) => setForm({ ...form, amount: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">Currency</Label>
              <CurrencySelect value={form.currency} showHomeHint={false}
                              testId="budget-currency"
                              onChange={(c) => setForm({ ...form, currency: c })} />
            </div>
          </div>
          {amending ? (
            <div>
              <Label className="text-xs">Why it is changing</Label>
              <Textarea rows={2} value={form.reason} data-testid="budget-reason"
                        placeholder="Kept on the line for the auditor"
                        onChange={(e) => setForm({ ...form, reason: e.target.value })} />
            </div>
          ) : null}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button disabled={!form.line || !(Number(form.amount) >= 0) || save.isPending}
                  onClick={() => save.mutate()} data-testid="budget-save">
            {save.isPending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}
            {amending ? 'Amend' : 'Set the line'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BudgetPanel;
